import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, CUSTOM_ELEMENTS_SCHEMA, effect, inject, input, InputSignal, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatError } from '@angular/material/form-field';
import { TripType } from '../../../interfaces/register-user.response';
import { BaseFormField } from '../../form/abstracts';
import { TripTypesFieldService } from './services/trip-types-field.service';

@Component({
  selector: 'app-trip-types-field',
  standalone: true,
  imports: [
    TranslateModule,
    MatError
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi:true,
      useExisting: TripTypesFieldComponent
    },
  ],
  templateUrl: './trip-types-field.component.html',
  styleUrls: ['./trip-types-field.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TripTypesFieldComponent extends BaseFormField<TripType[]> {
  // Inputs
  public form: InputSignal<FormGroup> = input.required<FormGroup>();
  public formControlName: InputSignal<string> = input.required<string>();
  
  // Services and utilities
  private _cdr = inject(ChangeDetectorRef);
  private _tripTypesFieldService = inject(TripTypesFieldService);
  
  // Computed properties
  private _formControlName = computed(() => this.form().get(this.formControlName()));
  
  // Local signals
  // ! PARA CICERONEWEB-SSR NO HARÁ FALTA SETEARLO POR EFFECT SEGÚN 
  // ! EL IDIOMA YA QUE CUANDO SE CAMBIA DE IDIOMA RECARGA LA PÁGINA
  // ! VIENE POR URL
  // ! EMT QUIERE ESTE ORDEN HARCODEADO
  // ! DEJO COMENTADO POR ORDEN ALFABÉTICO
  protected tripTypeListChips = signal<TripType[]>([]);
  
  // Effects
  /**
   * Sets up the trip type list with EMT-specific ordering
   * Maintains exact same logic as original, delegated to service
   */
  setTripTypeList = effect(() => {
    const orderedTripTypes = this._tripTypesFieldService.getOrderedTripTypes();
    this.tripTypeListChips.set(orderedTripTypes);
    console.log('Ordered Trip Types:', orderedTripTypes); // Debug log to verify order
  }, { allowSignalWrites: true });

  /**
   * Marks all form fields as touched for desktop validation
   * Maintains exact same logic as original, delegated to service
   */
  markAllFieldsAsTouchedForDesktop = effect(() => {
    const control = this._formControlName();
    this._tripTypesFieldService.markFieldsAsTouchedForDesktop(control, this._cdr);
  }, { allowSignalWrites: true });

  // Methods
  /**
   * Handles chip set trip type change event
   * Maintains exact same logic as original, delegated to service
   * @param event - The change event from the chip set
   */
  public handleChipSetTripTypeChange(event: Event) {
    const customEvent = event as CustomEvent<{ values: TripType }>;
    const selectedTripTypes = customEvent.detail.values[0]; // is single selection
    
    const control = this._formControlName();
    this._tripTypesFieldService.handleTripTypeSelection(selectedTripTypes, control);
  }
  
  /**
   * Checks if a trip type is currently selected
   * Maintains exact same logic as original, delegated to service
   * @param id - Trip type ID to check
   * @returns True if the trip type is selected
   */
  public isSelectedTripType(id: number): boolean {
    return this._tripTypesFieldService.isSelectedTripType(id);
  }
  
  /**
   * Validates if the field has errors and should display them
   * Maintains exact same validation logic as original, delegated to service
   * @returns True if field is invalid and should show errors
   */
  public isInvalidField(): boolean {
    const control = this._formControlName();
    return this._tripTypesFieldService.validateField(control);
  }
}